import { DialogCloseEvent, DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

const CART_DRAWER_VIEWED_EVENT = 'custom:cart_drawer_viewed';
const VIEW_CART_STAPE_EVENT = 'view_cart_stape';
const VIEW_CART_EVENT_ID_PREFIX = 'tytax_view_cart';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;
  /** @type {boolean} */
  #hasPublishedForOpen = false;
  /** @type {boolean} */
  #isPublishingViewCart = false;
  /** @type {number} */
  #viewCartPublishId = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#publishViewCartEvent);
    this.addEventListener(DialogCloseEvent.eventName, this.#resetViewCartPublishState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#publishViewCartEvent);
    this.removeEventListener(DialogCloseEvent.eventName, this.#resetViewCartPublishState);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #publishViewCartEvent = async () => {
    const analytics = window.Shopify?.analytics;

    if (this.#hasPublishedForOpen || this.#isPublishingViewCart) return;

    this.#isPublishingViewCart = true;
    const publishId = ++this.#viewCartPublishId;

    try {
      const cart = await this.#fetchCart();
      if (publishId !== this.#viewCartPublishId) return;

      const viewCartData = this.#mapCartToViewCartData(cart, this.#createViewCartEventId());
      this.#pushViewCartDataLayerEvent(viewCartData);
      this.#hasPublishedForOpen = true;

      if (typeof analytics?.publish === 'function') {
        await Promise.resolve(analytics.publish(CART_DRAWER_VIEWED_EVENT, viewCartData));
      }
    } catch (error) {
      if (publishId === this.#viewCartPublishId) {
        console.warn('Failed to publish cart drawer view event.', error);
      }
    } finally {
      if (publishId === this.#viewCartPublishId) {
        this.#isPublishingViewCart = false;
      }
    }
  };

  /**
   * @param {{ event_id: string, ecommerce: object, cart: object, source: string }} viewCartData
   */
  #pushViewCartDataLayerEvent(viewCartData) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: VIEW_CART_STAPE_EVENT,
        ...viewCartData,
      });
    } catch (error) {
      console.warn('Failed to push cart drawer view data layer event.', error);
    }
  }

  #resetViewCartPublishState = () => {
    this.#hasPublishedForOpen = false;
    this.#isPublishingViewCart = false;
    this.#viewCartPublishId += 1;
  };

  async #fetchCart() {
    const cartUrl =
      typeof Theme !== 'undefined' && Theme.routes?.cart_url ? `${Theme.routes.cart_url}.js` : '/cart.js';
    const response = await fetch(cartUrl, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);

    return response.json();
  }

  /**
   * @param {any} cart
   * @param {string} eventId
   */
  #mapCartToViewCartData(cart, eventId) {
    return {
      event_id: eventId,
      ecommerce: {
        currency: cart?.currency,
        value: this.#moneyFromCents(cart?.total_price),
        items: Array.isArray(cart?.items) ? cart.items.map(this.#mapCartItemToViewCartItem) : [],
      },
      cart: {
        item_count: cart?.item_count,
        total_price: cart?.total_price,
      },
      source: 'cart-drawer',
    };
  }

  #createViewCartEventId() {
    const randomValues = new Uint32Array(2);
    const crypto = window.crypto || window.msCrypto;

    if (typeof crypto?.getRandomValues === 'function') {
      crypto.getRandomValues(randomValues);
    } else {
      randomValues[0] = Math.floor(Math.random() * 0xffffffff);
      randomValues[1] = Math.floor(Math.random() * 0xffffffff);
    }

    return [
      VIEW_CART_EVENT_ID_PREFIX,
      Date.now().toString(36),
      randomValues[0].toString(36),
      randomValues[1].toString(36),
    ].join('_');
  }

  /**
   * @param {any} item
   */
  #mapCartItemToViewCartItem = (item) => ({
    item_id: this.#stringValue(item?.variant_id || item?.id),
    item_name: item?.product_title || item?.title,
    item_variant: item?.variant_title,
    shopify_product_id: this.#stringValue(item?.product_id),
    shopify_variant_id: this.#stringValue(item?.variant_id),
    price: this.#moneyFromCents(item?.final_price),
    quantity: item?.quantity,
    item_brand: item?.vendor,
    item_category: item?.product_type,
    item_sku: item?.sku,
  });

  /**
   * @param {unknown} value
   */
  #stringValue(value) {
    return value == null ? undefined : String(value);
  }

  /**
   * @param {unknown} value
   */
  #moneyFromCents(value) {
    return typeof value === 'number' ? value / 100 : undefined;
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
